import { KROT } from 'captcha-eu-sdk';
import { type ChangeEvent, type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Modal from '@/components/shared/Modal';
import Spinner from '@/components/shared/Spinner';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import apiService from '@/services/apiService';
import { notifyParent } from '@/services/parentMessenger';
import { validateEmail, validatePhone } from '@/services/validators';

const CAPTCHA_PUBLIC_KEY = import.meta.env.VITE_CAPTCHA_PUBLIC_KEY;

interface FormData {
	email: string;
	phone: string;
}

interface Errors {
	email?: string;
	phone?: string;
}

interface ContactDetailsDuringLoadProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

const ContactDetailsDuringLoad = ({ open, onOpenChange }: ContactDetailsDuringLoadProps) => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();

	const [formData, setFormData] = useState<FormData>({
		email: '',
		phone: ''
	});
	const [errors, setErrors] = useState<Errors>({});
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Setup KROT captcha on mount
	useEffect(() => {
		KROT.setup(CAPTCHA_PUBLIC_KEY);
	}, []);

	// Helper to get captcha solution
	const getCaptchaSolution = async (): Promise<string> => {
		// Skip captcha in e2e tests
		if (window.localStorage.getItem('__e2e__')) return '';
		const solution = await KROT.getSolution();
		return JSON.stringify(solution);
	};

	const validateForm = (): boolean => {
		const newErrors: Errors = {};

		// Email is required
		if (!formData.email.trim()) {
			newErrors.email = t('modals.contactDetailsDuringLoad.emailRequired');
		} else {
			const emailError = validateEmail(formData.email);
			if (emailError) {
				newErrors.email = t('modals.contactDetailsDuringLoad.emailError');
			}
		}

		// Phone is optional - only validate format if a value is entered
		if (formData.phone.trim()) {
			const phoneError = validatePhone(formData.phone);
			if (phoneError) {
				newErrors.phone = t('modals.contactDetailsDuringLoad.phoneError');
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { id, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[id]: value
		}));
		// Clear error when user starts typing
		if (errors[id as keyof Errors]) {
			setErrors((prev) => ({ ...prev, [id]: undefined }));
		}
	};

	const resetForm = useCallback(() => {
		setFormData({
			email: '',
			phone: ''
		});
		setErrors({});
	}, []);

	// Reset form when modal closes
	useEffect(() => {
		if (!open) {
			resetForm();
		}
	}, [open, resetForm]);

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		if (!id) {
			onOpenChange(false);
			return;
		}

		setIsSubmitting(true);

		try {
			const captchaSolution = await getCaptchaSolution();
			await apiService.contact.submit(
				id,
				formData.email,
				formData.phone,
				'', // empty name - incomplete data is OK
				captchaSolution
			);

			// Lead-tracking: the customer submitted their contact details.
			// No-op when not iframed or no parent origin was captured.
			notifyParent('contact_request');

			// Close modal immediately on success
			onOpenChange(false);
		} catch (error) {
			// Silently close modal on error (no feedback to user)
			console.warn(error);
			onOpenChange(false);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleSkip = () => {
		onOpenChange(false);
	};

	return (
		<Modal
			id="modalContactDetailsDuringLoad"
			open={open}
			onOpenChange={onOpenChange}
			backdrop="static"
			keyboard={false}
			closeButton={true}
		>
			<div className="py-6 px-4">
				<div className="text-2xl font-bold mb-2">{t('modals.contactDetailsDuringLoad.title')}</div>
				<p className="mb-6 text-muted-foreground leading-relaxed">
					{t('modals.contactDetailsDuringLoad.description')}
				</p>

				<form onSubmit={handleSubmit} noValidate className="space-y-4 text-left">
					{/* Email */}
					<Field data-invalid={!!errors.email}>
						<FieldLabel htmlFor="email">
							{t('modals.contactDetailsDuringLoad.emailLabel')}
						</FieldLabel>
						<Input
							type="email"
							id="email"
							placeholder={t('modals.contactDetailsDuringLoad.emailPlaceholder')}
							value={formData.email}
							onChange={handleInputChange}
							disabled={isSubmitting}
							className={errors.email ? 'border-destructive' : ''}
							data-testid="modal-contact-details-input-email"
							aria-invalid={!!errors.email}
						/>
						{errors.email && <FieldError>{errors.email}</FieldError>}
					</Field>

					{/* Phone */}
					<Field data-invalid={!!errors.phone}>
						<FieldLabel htmlFor="phone">
							{t('modals.contactDetailsDuringLoad.phoneLabel')}
						</FieldLabel>
						<Input
							type="tel"
							id="phone"
							placeholder={t('modals.contactDetailsDuringLoad.phonePlaceholder')}
							value={formData.phone}
							onChange={handleInputChange}
							disabled={isSubmitting}
							className={errors.phone ? 'border-destructive' : ''}
							data-testid="modal-contact-details-input-phone"
							aria-invalid={!!errors.phone}
						/>
						{errors.phone && <FieldError>{errors.phone}</FieldError>}
					</Field>

					{/* Buttons */}
					<div className="flex flex-col gap-3 pt-4">
						{/* Primary Submit Button */}
						<Button
							type="submit"
							disabled={isSubmitting}
							className="w-full"
							iconRight={
								isSubmitting ? <Spinner size="sm" variant="current" visible={isSubmitting} /> : null
							}
							data-testid="modal-contact-details-button-submit"
						>
							{t('modals.contactDetailsDuringLoad.submitButton')}
						</Button>

						{/* Secondary Skip Link */}
						<div className="text-center pt-2">
							<button
								type="button"
								onClick={handleSkip}
								disabled={isSubmitting}
								className="text-sm text-muted-foreground underline hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
								data-testid="modal-contact-details-button-skip"
							>
								{t('modals.contactDetailsDuringLoad.skipButton')}
							</button>
						</div>
					</div>
				</form>
			</div>
		</Modal>
	);
};

export default ContactDetailsDuringLoad;
